import {
  AddRounded,
  AdminPanelSettingsRounded,
  DeleteOutlineRounded,
  EditRounded,
  ErrorOutlineRounded,
  SearchRounded,
  VisibilityRounded,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  InputAdornment,
  TextField as MuiTextField,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import { RoleList } from "../../model/admin_roles";
import Loading from "../../../../components/loading/loading";
import useUser from "../../../../providers/user/user_provider";
import { add_roles, modify_role, view_role } from "../../../../routes/routes_path";
import { black, greyEnabled, purple, purpleLight, red, redLight } from "../../../../theme/colors";
import {
  TableEmptyState,
  TableFooterPagination,
} from "../../../users/components/table_toolkit/table_toolkit";
import { ModuleTab } from "../../hooks/use_module_tabs";
import OrgSelector from "../../../../components/org_selector/org_selector";

const ROWS_PER_PAGE = 8;

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

interface RoleListingParams {
  searchList: RoleList[];
  searchValue: string;
  setSearchValue: (val: string) => void;
  setViewSelectedRoleDetails: (val: boolean) => void;
  setSelectedRoleDetails: (val: RoleList) => void;
  roleList: RoleList[];
  setDeleteRole: (val: boolean) => void;
  currentTab: number;
  handleChange: (val: number) => void;
  page: number;
  setPage: (val: number) => void;
  count: number;
  isLoading?: boolean;
  isError?: boolean;
  tabs: ModuleTab[];
}

export default function RoleListingPage({
  searchValue,
  searchList,
  setSearchValue,
  setSelectedRoleDetails,
  setDeleteRole,
  currentTab,
  handleChange,
  page,
  count,
  setPage,
  isLoading = false,
  isError = false,
  tabs,
}: RoleListingParams) {
  const navigate = useNavigate();

  const startRange = page * ROWS_PER_PAGE + 1;
  const endRange = Math.min((page + 1) * ROWS_PER_PAGE, count);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const customText = `Showing ${count ? startRange : 0} to ${endRange || 0} of ${count || 0} entries`;
  const showList = !isLoading && !isError && searchList && searchList.length > 0;
  const showEmpty = !isLoading && !isError && (!searchList || searchList.length === 0);

  return (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        borderRadius: 3,
        border: "1px solid #ECECEC",
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={2}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: purpleLight,
                flexShrink: 0,
              }}
            >
              <AdminPanelSettingsRounded sx={{ color: purple, fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} color={black}>
                Manage Roles and Access
              </Typography>
              <Typography variant="body2" color={greyEnabled}>
                Define roles and the module access they grant.
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
            <OrgSelector lockToOwnOrg />
            <Button
              onClick={() => navigate(add_roles)}
              variant="contained"
              disableElevation
              startIcon={<AddRounded sx={{ height: 18, width: 18 }} />}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Add Admin Role
            </Button>
          </Box>
        </Box>

        <Tabs
          value={currentTab}
          onChange={(_, currentTabVal: number) => handleChange(currentTabVal)}
          variant="scrollable"
          scrollButtons="auto"
          TabIndicatorProps={{
            style: { backgroundColor: purple, height: 3, borderRadius: 3 },
          }}
          sx={{ borderBottom: "1px solid #ECECEC", mt: 3 }}
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              label={tab.label}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: greyEnabled,
                "&.Mui-selected": { color: purple },
              }}
            />
          ))}
        </Tabs>

        <Box mt={3} mb={2} maxWidth={360}>
          <TextField
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setPage(0);
            }}
            size="small"
            fullWidth
            placeholder="Search by role"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded sx={{ color: greyEnabled, fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <Loading />
          </Box>
        ) : isError ? (
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            py={8}
            gap={0.5}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 44,
                height: 44,
                borderRadius: "50%",
                bgcolor: redLight,
                mb: 1,
              }}
            >
              <ErrorOutlineRounded sx={{ color: red, fontSize: 24 }} />
            </Box>
            <Typography fontWeight={600} color={black}>
              Something went wrong
            </Typography>
            <Typography variant="body2" color={greyEnabled}>
              We couldn't load the roles list. Please try again.
            </Typography>
          </Box>
        ) : showEmpty ? (
          <TableEmptyState
            message="No roles found"
            hint="Try adjusting your search or filters."
          />
        ) : (
          <Grid container spacing={2} mt={0.5}>
            {searchList?.map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 2,
                    border: "1px solid #ECECEC",
                    boxShadow: "0 4px 14px rgba(17,17,17,0.1)",
                    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      borderColor: purple,
                      boxShadow: "0 8px 20px rgba(17,17,17,0.14)",
                    },
                  }}
                >
                  <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5, flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={1.5} sx={{ opacity: item?.isActive ? 1 : 0.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: purpleLight,
                          flexShrink: 0,
                        }}
                      >
                        <AdminPanelSettingsRounded sx={{ color: purple, fontSize: 20 }} />
                      </Box>
                      <Typography fontWeight={600} color={black} noWrap>
                        {item.role}
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      gap={0.5}
                      mt="auto"
                      pt={1.5}
                      sx={{ borderTop: "1px solid #F1E9F1" }}
                    >
                      <Tooltip title="View details">
                        <IconButton
                          onClick={() => navigate(`${view_role}/${item.id}`)}
                          sx={{
                            bgcolor: "#E7F5EC",
                            "&:hover": { bgcolor: "#D6EEE0" },
                            opacity: item?.isActive ? 1 : 0.4,
                          }}
                        >
                          <VisibilityRounded sx={{ color: "#1E6446", fontSize: 20 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={item?.isSuperAdmin ? "Super admin role can't be edited" : "Edit role"}>
                        <span>
                          <IconButton
                            disabled={item?.isSuperAdmin}
                            onClick={() => navigate(`${modify_role}/${item.id}`)}
                            sx={{
                              bgcolor: purpleLight,
                              "&:hover": { bgcolor: "#F0D9F0" },
                              opacity: item?.isActive ? 1 : 0.4,
                              "&.Mui-disabled": { bgcolor: "#F2F2F2" },
                            }}
                          >
                            <EditRounded sx={{ color: item?.isSuperAdmin ? "#B9B4B9" : purple, fontSize: 20 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={item?.isSuperAdmin ? "Super admin role can't be deleted" : "Delete role"}>
                        <span>
                          <IconButton
                            disabled={item?.isSuperAdmin}
                            onClick={() => {
                              if (item?.isActive) {
                                setDeleteRole(true);
                                setSelectedRoleDetails?.(item);
                              }
                            }}
                            sx={{
                              bgcolor: redLight,
                              "&:hover": { bgcolor: "#FBD9D6" },
                              opacity: item?.isActive ? 1 : 0.4,
                              "&.Mui-disabled": { bgcolor: "#F2F2F2" },
                            }}
                          >
                            <DeleteOutlineRounded sx={{ color: item?.isSuperAdmin ? "#B9B4B9" : red, fontSize: 20 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>

      {showList && (
        <CardActions sx={{ px: 4, pb: 3 }}>
          <Box width="100%">
            <TableFooterPagination
              count={count}
              page={page}
              onPageChange={handleChangePage}
              summaryText={customText}
              rowsPerPage={ROWS_PER_PAGE}
            />
          </Box>
        </CardActions>
      )}
    </Card>
  );
}
